// Core packages
import { formatDate } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';

// Third party packages
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from "file-saver";
import { combineLatest, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, startWith, tap } from 'rxjs/operators';

// Core packages
import { MatOptionSelectionChange } from '@angular/material';
import { AuthService } from 'src/app/modules/auth/auth.service';
import { BalancesService } from 'src/app/modules/balances/balances.service';
import { CompaniesService } from 'src/app/modules/companies/companies.service';
import { PropertiesService } from 'src/app/modules/properties/properties.service';
import { ListSetting } from 'src/app/shared/interfaces/list-setting.interface';
import { ICompany } from 'src/app/shared/models/company/company.interface';
import { PropertyInterface } from 'src/app/shared/models/property/property.interface';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from 'src/app/shared/services/helper.service';
import { ListsService } from 'src/app/shared/services/lists.service';
import { ThemeService } from 'src/app/shared/services/theme.service';
import { BalancesDataSource } from 'src/app/shared/utils/data-sources/balances-data-source';

@Component({
  selector: 'app-balances-list-first',
  templateUrl: './balances-list-first.page.html',
  styleUrls: ['./balances-list-first.page.scss']
})
export class BalancesListFirstComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];

  // BEGIN - Table balances
  displayedColumns: string[] = ['index', 'number', 'company', 'property', 'status', 'total', 'totalDocsInserted', 'createdAt', 'actions'];
  dataSource: BalancesDataSource;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  // @ViewChild('input', { static: true }) input: ElementRef;
  numberFilter: FormControl = new FormControl('');
  companyFilter: FormControl = new FormControl('');
  propertyFilter: FormControl = new FormControl('');
  totalFilter: FormControl = new FormControl('');
  totalDocsInsertedFilter: FormControl = new FormControl('');
  createdAtFilter: FormControl = new FormControl('');
  // END - Table balances

  activeRoute: string;
  propertyIsLoading = false;
  availableProperties: PropertyInterface[] = [];
  companyIsLoading = false;
  availableCompanies: ICompany[] = [];

  // Filter on SAG company
  companySag = true;

  /**
   * Class constructor
   */
  constructor(
    private balancesService: BalancesService,
    private helperService: HelperService,
    private authService: AuthService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private listsService: ListsService,
    private propertiesService: PropertiesService,
    private companiesService: CompaniesService,
    private configService: ConfigService,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(this.translateService.instant('balances.list-first.pageTitle'));

    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser = res));
    this.subscriptions.push(this.authService.loggedUserPermissions$.subscribe(res => this.loggedUserPermissions = res));

    // Init data source
    this.dataSource = new BalancesDataSource(this.balancesService, this.helperService);

    // Subscribe to listsService and update this list settings
    this.subscribeToListsSettings('balances');

    // Get available admin and owners from users' list
    this.getAvailableProperties();
    this.getAvailableCompanies();
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => this.loadPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through general search
    // this.subscriptions.push(
    //   fromEvent(this.input.nativeElement, 'keyup')
    //     .pipe(
    //       debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
    //       distinctUntilChanged(), // Eliminate duplicate values
    //       tap(() => {
    //         this.paginator.pageIndex = 0; // Reset page
    //         this.loadPage();
    //       })
    //     )
    //     .subscribe()
    // );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.numberFilter.valueChanges.pipe(startWith(null as string)),
        this.propertyFilter.valueChanges.pipe(startWith(null as string)),
        this.companyFilter.valueChanges.pipe(startWith(null as string)),
        this.totalFilter.valueChanges.pipe(startWith(null as string)),
        this.totalDocsInsertedFilter.valueChanges.pipe(startWith(null as string)),
        this.createdAtFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(res => {
          // Handle property filter
          if (res) {
            this.propertyIsLoading = true;
            this.getAvailableProperties({
              general: this.propertyFilter.value
            });

            this.companyIsLoading = true;
            this.getAvailableCompanies({
              general: this.companyFilter.value
            });
          }

          // Load page only if at least one value is not null
          if (!res.every(value => value === null)) {
            this.paginator.pageIndex = 0;
            this.loadPage();
          }
        }
        )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all balances
    this.subscriptions.forEach(sub => sub.unsubscribe());

    // Push current list settings to the listsService
    this.listsService.setListSetting(
      'balances',
      this.paginator.pageIndex.valueOf(),
      this.paginator.pageSize.valueOf(),
      {
        general: '', // this.input.nativeElement.value,
        number: this.numberFilter.value,
        property: this.propertyFilter.value,
        company: this.companyFilter.value,
        total: this.totalFilter.value,
        totalDocsInserted: this.totalDocsInsertedFilter.value,
        createdAt: this.createdAtFilter.value,
      }
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: '', // this.input.nativeElement.value,
      number: this.numberFilter.value,
      property: this.propertyFilter.value ? this.propertyFilter.value._id : undefined,
      company: this.companyFilter.value ? this.companyFilter.value._id : undefined,
      companySag: this.companySag,
      status: 'open-first',
      total: this.totalFilter.value,
      totalDocsInserted: this.totalDocsInsertedFilter.value,
      createdAt: this.createdAtFilter.value ? new Date(this.createdAtFilter.value).toISOString() : undefined,
    };
    this.dataSource.loadItems(start, length, this.sort.active, this.sort.direction, search);
  }

  /**
   * Export balances in a file with given format
   *
   * @since 1.0.0
   */
  onExport(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('balances.list-first.exportFileName')} ${date}`;
    this.subscriptions.push(
      this.balancesService.getExportOpenFirst().subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting('balances', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.numberFilter.setValue(null);
    this.propertyFilter.setValue(null);
    this.companyFilter.setValue(null);
    this.totalFilter.setValue(null);
    this.totalDocsInsertedFilter.setValue(null);
    this.createdAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.sort.direction = "desc";
    this.sort.active = "number";
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadPage();
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe(
        (settings: ListSetting[]) => {
          if (settings.length) {
            settings.forEach((setting: ListSetting) => {
              if (setting.name === name) {
                if (setting.pageIndex) {
                  this.paginator.pageIndex = setting.pageIndex;
                }
                if (setting.pageSize) {
                  this.paginator.pageSize = setting.pageSize;
                }
                if (setting.search && setting.search.general) {
                  // this.input.nativeElement.value = setting.search.general;
                }
                if (setting.search && setting.search.number) {
                  this.numberFilter.setValue(setting.search.number);
                }
                if (setting.search && setting.search.property) {
                  this.propertyFilter.setValue(setting.search.property);
                }
                if (setting.search && setting.search.company) {
                  this.companyFilter.setValue(setting.search.company);
                }
                if (setting.search && setting.search.total) {
                  this.totalFilter.setValue(setting.search.total);
                }
                if (setting.search && setting.search.totalDocsInserted) {
                  this.totalDocsInsertedFilter.setValue(setting.search.totalDocsInserted);
                }
                if (setting.search && setting.search.createdAt) {
                  this.createdAtFilter.setValue(setting.search.createdAt);
                }
              }
            });
          }
        }
      )
    );
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = '';
    const event = new KeyboardEvent('keyup');
    // this.input.nativeElement.dispatchEvent(event);
  }


  /**
   * Get the list of available admins and owners
   * used to allow filtering on owner and admin columns
   *
   * @since 1.0.0
   */
  getAvailableProperties(search?: any): void {
    this.subscriptions.push(
      this.propertiesService
        .getList(0, null, null, null, search, 'both') // get both active/inactive properties
        .pipe(
          finalize(() => {
            this.propertyIsLoading = false;
          })
        )
        .subscribe(res => {
          if (res.status) {
            this.availableProperties = res.data.items;
          }
        })
    );
  }

  /**
   * Get the list of available admins and owners
   * used to allow filtering on owner and admin columns
   *
   * @since 1.0.0
   */
  getAvailableCompanies(search?: any): void {
    this.subscriptions.push(
      this.companiesService
        .getList(0, null, null, null, search)
        .pipe(
          finalize(() => {
            this.companyIsLoading = false;
          })
        )
        .subscribe(res => {
          if (res.status) {
            this.availableCompanies = res.data.items;
          }
        })
    );
  }

  /**
   * Used to handle the display of selected result
   *
   * @since 1.0.0
   *
   * @param arg Data to be displayed
   * @returns result string to display or undefined
   */
  displayFn(arg?: PropertyInterface): string | undefined {
    return arg ? `${arg.name}` : undefined;
  }

  /**
   * Used to handle the display of selected result
   *
   * @since 1.0.0
   *
   * @param arg Data to be displayed
   * @returns result string to display or undefined
   */
  displayCompanyFn(arg?: ICompany): string | undefined {
    return arg ? `${arg.name}` : undefined;
  }

  /**
   * Handle click on a selected property in the mat-autocomplete list and fires the loadPage()
   *
   * @since 1.0.0
   */
  onPropertyFilterSelected(event: MatOptionSelectionChange): void {
    const value = event.source.value;
    this.propertyFilter.setValue(value, { emitEvent: false });
    this.loadPage();
  }

  /**
   * Handle click on a selected company in the mat-autocomplete list and fires the loadPage()
   *
   * @since 1.0.0
   */
  onCompanyFilterSelected(event: MatOptionSelectionChange): void {
    const value = event.source.value;
    this.companyFilter.setValue(value, { emitEvent: false });
    this.loadPage();
  }

  /**
   * Handle click show/hide SAG balances
   *
   * @since 1.0.0
   */
  onToggleSAG(): void {
    this.companySag = !this.companySag;
    this.loadPage();
  }
}
